import { Spacing } from '@persona-id/design-tokens';
import { CircleHelpIcon } from '@persona-id/design-tokens/icons';
import { ComponentPropsWithoutRef, useState } from 'react';
import { FormError } from 'shared/components/FormControl';
import styled from 'styled-components';

import ModalWrapper from 'dashboard/components/ModalWrapper';
import {
  ADVERSE_MEDIA_CATEGORY_OPTIONS,
  ADVERSE_MEDIA_CATEGORY_TYPE_DISPLAY_MAP,
} from 'dashboard/forms/FormReportTemplate/AdverseMediaCategoriesSelect/adverseMediaSources';
import {
  SearchListOption,
  SearchListsSelectTable,
} from 'dashboard/forms/FormReportTemplate/SearchListsSelectTable';
import {
  ModalBody,
  ModalContainer,
  ModalDescription,
  ModalHeader,
} from 'dashboard/layouts/ModalLayout';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import Spacer from 'design-system/components/Spacer';
import { Label, Span } from 'design-system/components/Text';
import Tooltip from 'design-system/components/Tooltip';
import { BASE } from 'design-system/variables/spacing';
import { FontWeight } from 'design-system/variables/text';
import useToggle from 'lib/hooks/useToggle';

export interface AdverseMediaCategoryOption extends SearchListOption {
  info?: string;
}

interface AdverseMediaCategoriesSelectProps
  extends Omit<ComponentPropsWithoutRef<'select'>, 'onChange'> {
  disabled?: boolean;
  error?: string;
  value: string[];
  onChange: (values: string[]) => void;
}

export const AdverseMediaCategoriesSelect = ({
  value,
  disabled,
  error,
  onChange,
  onBlur,
  name,
}: AdverseMediaCategoriesSelectProps) => {
  const [isModalOpen, { enable: showModal, disable: hideModal }] = useToggle(false);
  const [selected, setSelected] = useState(value);

  return (
    <>
      <Card>
        <StyledBody>
          <EditButton
            id='adverse-media-categories-select__edit'
            variant='basic'
            width='auto'
            size='sm'
            type='button'
            disabled={disabled}
            onClick={(event) => {
              event.stopPropagation();
              showModal();
            }}
          >
            Edit
          </EditButton>
          <StyledLabel size='lg'>Categories</StyledLabel>
          <Span>Current categories being used to check for matches in this template.</Span>
          <Spacer height={1} />
          <SearchListsSelectTable
            options={ADVERSE_MEDIA_CATEGORY_OPTIONS}
            values={selected}
            viewMode={true}
            valueDisplayName='Category name'
            categoryDisplayName='Compliance'
            categoryDisplayMap={ADVERSE_MEDIA_CATEGORY_TYPE_DISPLAY_MAP}
            optionDisplay={(option: AdverseMediaCategoryOption) => {
              return (
                <Tooltip
                  portal={false}
                  side='right'
                  trigger={
                    <OptionDisplay padding={BASE}>
                      {option.name}
                      <StyledCircleHelpIcon />
                    </OptionDisplay>
                  }
                >
                  <TooltipWrapper>{option.info}</TooltipWrapper>
                </Tooltip>
              );
            }}
          />
          {!!error && <FormError>{error}</FormError>}
        </StyledBody>
      </Card>
      <select name={name} disabled={disabled} onBlur={onBlur} multiple hidden>
        {ADVERSE_MEDIA_CATEGORY_OPTIONS.map((option) => (
          <option key={option.value} value={option.value}>
            {option.value}
          </option>
        ))}
      </select>
      <ModalWrapper isOpen={isModalOpen}>
        <ModalContainer size='lg'>
          <ModalHeader onClose={hideModal}>Edit categories</ModalHeader>
          <ModalDescription>Edit categories for this report template.</ModalDescription>
          <ModalBody>
            <SearchListsSelectTable
              options={ADVERSE_MEDIA_CATEGORY_OPTIONS}
              values={selected}
              valueDisplayName='Category name'
              categoryDisplayName='Compliance'
              categoryDisplayMap={ADVERSE_MEDIA_CATEGORY_TYPE_DISPLAY_MAP}
              headerWidth='50%'
              optionDisplay={(option: AdverseMediaCategoryOption) => {
                return (
                  <Tooltip
                    portal={false}
                    side='right'
                    trigger={
                      <OptionDisplay padding={BASE}>
                        {option.name}
                        <StyledCircleHelpIcon />
                      </OptionDisplay>
                    }
                  >
                    <TooltipWrapper>{option.info}</TooltipWrapper>
                  </Tooltip>
                );
              }}
              onChange={setSelected}
            />
            <Spacer height={2} />
            <StyledFooter>
              <Button
                id='adverse-media-categories-select__cancel'
                variant='basic'
                width='auto'
                onClick={hideModal}
              >
                Cancel
              </Button>
              <Button
                id='adverse-media-categories-select__submit'
                variant='primary'
                width='auto'
                onClick={(event) => {
                  event.preventDefault();
                  onChange(selected);
                  hideModal();
                }}
              >{`Apply (${selected?.length})`}</Button>
            </StyledFooter>
          </ModalBody>
        </ModalContainer>
      </ModalWrapper>
    </>
  );
};

const TooltipWrapper = styled.div`
  max-width: 300px;
`;

const EditButton = styled(Button)`
  position: absolute;
  top: ${Spacing[5]};
  right: ${Spacing[5]};
`;

const StyledLabel = styled(Label)`
  font-weight: ${FontWeight.Bold};
`;

const StyledBody = styled(Card.Body)`
  position: relative;
`;

const StyledCircleHelpIcon = styled(CircleHelpIcon)`
  color: ${({ theme }) => theme.color.text.tertiary};
`;

const OptionDisplay = styled.span<{ padding: number }>`
  display: grid;
  grid-gap: ${Spacing[1]};
  grid-template-columns: auto auto;
  align-items: center;
  padding: ${({ padding }) => padding}px 0;
`;

const StyledFooter = styled.footer`
  display: grid;
  justify-content: end;
  grid-template-columns: auto auto;
  grid-column-gap: ${BASE * 2.5}px;
`;
